import { buildApiPath } from "./api-contracts";
import {
  coachingResourcesReadPath,
} from "./generated/coaching-resources-read-client";
import {
  ensureLiveOrgId,
  requiresLiveOrgId,
  withLiveOrgId
} from "./coaching-services-transport";
import { getCoachingOrgId } from "./coaching-org-context";
import { coachingEndpoints } from "./coaching-contracts";
import {
  liveResourceRow,
  toLiveListResponse,
  toLiveRecordResponse,
} from "./coaching-live-readmodels";
import {
  type Query,
  type Requester,
  list,
  operationContract as operation,
  record,
  requestContract
} from "./coaching-services-transport";
import type {
  CoachingAssignableCoachDto,
  CoachingCohortDto,
  CoachingCompanyDto,
  CoachingCursorListQuery,
  CoachingHistoryEventDto,
  CoachingProgramDto,
  CoachingRecordInput,
  CoachingResourceDto,
  CoachingUserDto,
  ListResponse,
  ResourceUploadOperationRequest
} from "./coaching-dtos";

async function readGeneratedResource<T>(
  requester: Requester,
  operation: "resourceList" | "resourceDetail",
  options: { resourceId?: string; query?: Query } = {}
): Promise<T> {
  const contract = coachingEndpoints[operation === "resourceList" ? "resources.list" : "resources.detail"];
  if (requiresLiveOrgId(contract) && !getCoachingOrgId()) await ensureLiveOrgId(requester);
  const path = coachingResourcesReadPath(
    operation,
    options.resourceId === undefined ? {} : { resource_id: options.resourceId }
  );
  const payload = await requester.request<unknown>(
    buildApiPath(path, {}, withLiveOrgId(contract, options.query)),
    { method: "GET" }
  );
  if (operation === "resourceList") {
    return toLiveListResponse(payload, liveResourceRow) as T;
  }
  return toLiveRecordResponse(payload, liveResourceRow) as T;
}

export function createCompaniesService(requester: Requester) {
  return {
    list: (query?: CoachingCursorListQuery) => list<CoachingCompanyDto>(requester, "companies.list", query),
    detail: (companyId: string) =>
      record<CoachingCompanyDto>(requester, "companies.detail", { pathParams: { company_id: companyId } }),
    create: (body: CoachingRecordInput, idempotencyKey: string) =>
      record<CoachingCompanyDto>(requester, "companies.create", { body, idempotencyKey }),
    update: (companyId: string, body: CoachingRecordInput, idempotencyKey: string) =>
      record<CoachingCompanyDto>(requester, "companies.update", {
        pathParams: { company_id: companyId },
        body,
        idempotencyKey
      })
  };
}

/**
 * F03: the Programs page and the program detail read ask for archived programs too, so an
 * archived program stays listed and can be reactivated from its edit form. Program pickers and
 * exports keep the API's active-only default.
 */
export const PROGRAMS_WITH_ARCHIVED_QUERY = { include_archived: "true" } as const;

export function createProgramsService(requester: Requester) {
  return {
    list: (query?: Query) => list<CoachingProgramDto>(requester, "programs.list", query),
    detail: (programId: string) =>
      record<CoachingProgramDto>(requester, "programs.detail", {
        pathParams: { program_id: programId },
        query: PROGRAMS_WITH_ARCHIVED_QUERY
      }),
    create: (body: CoachingRecordInput, idempotencyKey: string) =>
      record<CoachingProgramDto>(requester, "programs.create", { body, idempotencyKey }),
    update: (programId: string, body: CoachingRecordInput, idempotencyKey: string) =>
      record<CoachingProgramDto>(requester, "programs.update", {
        pathParams: { program_id: programId },
        body,
        idempotencyKey
      })
  };
}

export function createCohortsService(requester: Requester) {
  return {
    list: (query?: CoachingCursorListQuery) => list<CoachingCohortDto>(requester, "cohorts.list", query),
    detail: (cohortId: string) =>
      record<CoachingCohortDto>(requester, "cohorts.detail", { pathParams: { cohort_id: cohortId } }),
    create: (body: CoachingRecordInput, idempotencyKey: string) =>
      record<CoachingCohortDto>(requester, "cohorts.create", { body, idempotencyKey }),
    update: (cohortId: string, body: CoachingRecordInput, idempotencyKey: string) =>
      record<CoachingCohortDto>(requester, "cohorts.update", {
        pathParams: { cohort_id: cohortId },
        body,
        idempotencyKey
      }),
    complete: (cohortId: string, idempotencyKey: string) =>
      // The live route validates the body explicitly: completion is refused
      // without { confirm: true } (400 CO_VALIDATION_FAILED).
      operation(requester, "cohorts.complete", {
        pathParams: { cohort_id: cohortId },
        body: { confirm: true },
        idempotencyKey
      }),
    progress: (cohortId: string) =>
      operation(requester, "cohorts.progress", { pathParams: { cohort_id: cohortId } }),
    reportReadiness: (cohortId: string) =>
      operation(requester, "cohorts.reportReadiness", {
        pathParams: { cohort_id: cohortId }
      })
  };
}

export function createResourcesService(requester: Requester) {
  return {
    list: (query?: Query) => readGeneratedResource<{ items: CoachingResourceDto[]; next_cursor?: string | null }>(requester, "resourceList", { query }),
    detail: (resourceId: string) => readGeneratedResource<CoachingResourceDto>(requester, "resourceDetail", { resourceId }),
    create: (body: CoachingRecordInput, idempotencyKey: string) =>
      record<CoachingResourceDto>(requester, "resources.create", { body, idempotencyKey }),
    update: (resourceId: string, body: CoachingRecordInput, idempotencyKey: string) =>
      record<CoachingResourceDto>(requester, "resources.update", {
        pathParams: { resource_id: resourceId },
        body,
        idempotencyKey
      }),
    completeUpload: (
      resourceId: string,
      uploadId: string,
      body: ResourceUploadOperationRequest,
      idempotencyKey: string
    ) =>
      operation(requester, "resources.completeUpload", {
        pathParams: { resource_id: resourceId, upload_id: uploadId },
        body,
        idempotencyKey
      }),
    requestDownload: (
      resourceId: string,
      uploadId: string,
      idempotencyKey: string,
      body?: ResourceUploadOperationRequest
    ) =>
      // The live route validates an UPLOADS preflight body even for the
      // read-shaped download operation.
      operation(requester, "resources.requestDownload", {
        pathParams: { resource_id: resourceId, upload_id: uploadId },
        body,
        idempotencyKey
      }),
    deleteUpload: (
      resourceId: string,
      uploadId: string,
      idempotencyKey: string,
      body?: ResourceUploadOperationRequest
    ) =>
      operation(requester, "resources.deleteUpload", {
        pathParams: { resource_id: resourceId, upload_id: uploadId },
        body,
        idempotencyKey
      })
  };
}

export function createHistoryService(requester: Requester) {
  return {
    list: (query?: Query) => list<CoachingHistoryEventDto>(requester, "history.list", query),
    detail: (eventId: string) =>
      record<CoachingHistoryEventDto>(requester, "history.detail", { pathParams: { event_id: eventId } })
  };
}

export function createUsersService(requester: Requester) {
  return {
    list: (query?: Query) => list<CoachingUserDto>(requester, "users.list", query),
    detail: (userId: string) =>
      record<CoachingUserDto>(requester, "users.detail", { pathParams: { user_id: userId } }),
    assignableCoaches: async (query?: Query): Promise<ListResponse<CoachingAssignableCoachDto>> => {
      const response = await requestContract<{ coaches?: Array<{ id: string; displayName: string }> }>(requester, "users.assignableCoaches", { query });
      return {
        items: (response.coaches ?? []).map((coach) => ({ id: String(coach.id), name: coach.displayName, role: "coach" as const })),
        next_cursor: null
      };
    },
    create: (body: CoachingRecordInput, idempotencyKey: string) =>
      record<CoachingUserDto>(requester, "users.create", { body, idempotencyKey }),
    update: (userId: string, body: CoachingRecordInput, idempotencyKey: string) =>
      record<CoachingUserDto>(requester, "users.update", {
        pathParams: { user_id: userId },
        body,
        idempotencyKey
      }),
    deactivate: (userId: string, idempotencyKey: string) =>
      operation(requester, "users.deactivate", {
        pathParams: { user_id: userId },
        idempotencyKey
      }),
    zoomStatus: (userId: string) =>
      operation(requester, "users.zoomStatus", { pathParams: { user_id: userId } })
  };
}
